
import { UserProfile, UserRole } from "../types";
import { createClient } from '@supabase/supabase-js';
import { encryptionService } from "./encryptionService";

const SUPABASE_URL = (window as any).process?.env?.SUPABASE_URL ;
const SUPABASE_ANON_KEY = (window as any).process?.env?.SUPABASE_ANON_KEY ;

export const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY);
const SESSION_KEY = 'nominee_app_session';
const VAULT_SECRET_KEY = 'nominee_vault_secret'; 
export const GOOGLE_AUTH_SECRET = 'GOOGLE_SOCIAL_PROVIDER';

/**
 * Maps a UserProfile object to database column names (snake_case)
 */
const mapProfileToDb = (profile: UserProfile) => ({
  full_name: profile.name,
  email: profile.email,
  avatar_url: profile.avatar,
  phone: profile.phone,
  address: profile.address,
  correspondence_address: profile.correspondenceAddress,
  dob: profile.dob,
  gender: profile.gender,
  nationality: profile.nationality,
  marital_status: profile.maritalStatus,
  pan_last_5: profile.panLast5,
  aadhaar_last_4: profile.aadhaarLast4,
  passport_number: profile.passportNumber,
  voter_id: profile.voterId,
  occupation: profile.occupation,
  employer: profile.employer,
  annual_income_range: profile.annualIncomeRange,
  is_google_linked: profile.isGoogleLinked,
  is_verified: profile.isVerified
});

/**
 * Maps database columns (snake_case) back to UserProfile object
 */
const mapDbToProfile = (dbRow: any, role?: UserRole): UserProfile => ({
  id: dbRow.user_id,
  name: dbRow.full_name || '',
  email: dbRow.email || '',
  avatar: dbRow.avatar_url,
  phone: dbRow.phone,
  address: dbRow.address,
  correspondenceAddress: dbRow.correspondence_address,
  dob: dbRow.dob,
  gender: dbRow.gender,
  nationality: dbRow.nationality,
  maritalStatus: dbRow.marital_status,
  panLast5: dbRow.pan_last_5,
  aadhaarLast4: dbRow.aadhaar_last_4,
  passportNumber: dbRow.passport_number,
  voterId: dbRow.voter_id,
  occupation: dbRow.occupation,
  employer: dbRow.employer,
  annualIncomeRange: dbRow.annual_income_range,
  // Fix: changed snake_case keys to camelCase to match UserProfile interface
  isGoogleLinked: dbRow.is_google_linked,
  isVerified: dbRow.is_verified,
  role: role
});

export const authService = {
  async register(name: string, email: string, password: string, role: UserRole = 'owner'): Promise<{ success: boolean; message?: string }> {
    try {
      const emailLower = email.toLowerCase();
      
      const { data: existing } = await supabase
        .from('user_identities')
        .select('id')
        .eq('email', emailLower)
        .single();

      if (existing) return { success: false, message: 'Identity already initialized.' };

      const authHash = await encryptionService.hashForAuth(password, emailLower);

      // 1. Create Identity (Security Table)
      const { data: identity, error: identityError } = await supabase
        .from('user_identities')
        .insert({
          email: emailLower,
          auth_hash: authHash,
          role: role
        })
        .select()
        .single();

      if (identityError) throw identityError;

      // 2. Initialize Profile (Information Table - Separate Fields)
      const profile: UserProfile = {
        name,
        email: emailLower,
        isGoogleLinked: password.startsWith(GOOGLE_AUTH_SECRET),
        isVerified: true,
        role: role
      };

      const { error: infoError } = await supabase
        .from('user_information')
        .insert({
          user_id: identity.id,
          ...mapProfileToDb(profile)
        });

      if (infoError) throw infoError;

      // 3. Initialize empty Vault (Encrypted)
      await supabase.from('user_vaults').insert({ 
        user_id: identity.id, 
        data: await encryptionService.encrypt({ people: [], assets: [] }, password) 
      });

      return { success: true };
    } catch (e: any) {
      console.error("Registration Error:", e);
      return { success: false, message: 'Identity registration failed.' };
    }
  },

  async login(email: string, password: string): Promise<{ success: boolean; user?: UserProfile; message?: string }> {
    try {
      const emailLower = email.toLowerCase();
      
      const { data: identity, error: identityError } = await supabase
        .from('user_identities')
        .select('id, auth_hash, role')
        .eq('email', emailLower)
        .single();

      if (identityError || !identity) return { success: false, message: 'Identity not found.' };

      const calculatedHash = await encryptionService.hashForAuth(password, emailLower);
      if (identity.auth_hash !== calculatedHash) return { success: false, message: 'Cryptographic handshake failed.' };

      // Fetch Profile from user_information (Separate Fields)
      const { data: info, error: infoError } = await supabase
        .from('user_information')
        .select('*')
        .eq('user_id', identity.id)
        .single();

      if (infoError || !info) return { success: false, message: 'Profile data missing.' };

      const userProfile = mapDbToProfile(info, identity.role);

      localStorage.setItem(SESSION_KEY, JSON.stringify(userProfile));
      sessionStorage.setItem(VAULT_SECRET_KEY, password);

      return { success: true, user: userProfile };
    } catch (e: any) {
      console.error("Login Error:", e);
      return { success: false, message: 'Authentication error.' };
    }
  },

  async updateProfile(user: UserProfile): Promise<boolean> {
    if (!user.id) return false;
    try {
      const dbPayload = mapProfileToDb(user);
      
      const { error } = await supabase
        .from('user_information')
        .update({ 
          ...dbPayload, 
          updated_at: new Date().toISOString() 
        })
        .eq('user_id', user.id);
      
      if (!error) {
        localStorage.setItem(SESSION_KEY, JSON.stringify(user));
        return true;
      }
      console.error("Profile update error:", error);
    } catch (e) {
      console.error("Profile update failed:", e);
    }
    return false;
  },

  logout() {
    localStorage.removeItem(SESSION_KEY);
    sessionStorage.removeItem(VAULT_SECRET_KEY);
  },

  getCurrentSession(): UserProfile | null {
    const session = localStorage.getItem(SESSION_KEY);
    if (!session) return null;
    try { return JSON.parse(session); } catch { return null; }
  },

  getVaultSecret(): string | null {
    return sessionStorage.getItem(VAULT_SECRET_KEY);
  }
};
